package dev.lingvo.site.pages.app.projects.slug

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import dev.lingvo.shared.MtEstimateDto
import dev.lingvo.shared.MtJobDto
import dev.lingvo.shared.MtJobState
import dev.lingvo.shared.localeInfo
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LChip
import dev.lingvo.site.ds.LFieldLabelStyle
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LPauseIcon
import dev.lingvo.site.ds.LReceiptIcon
import dev.lingvo.site.ds.LSectionHeading
import dev.lingvo.site.ds.LStatDelta
import dev.lingvo.site.ds.LStatTile
import dev.lingvo.site.ds.LTable
import dev.lingvo.site.ds.LTableBody
import dev.lingvo.site.ds.LTableCell
import dev.lingvo.site.ds.LTableHead
import dev.lingvo.site.ds.LTableHeaderCell
import dev.lingvo.site.ds.LTableRow
import dev.lingvo.site.ds.LTaskProgress
import dev.lingvo.site.ds.LToggleChip
import dev.lingvo.site.ds.LZapIcon
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.formatCount
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * The languages, worst first, each carrying what it is behind by.
 *
 * "All / None / Behind only" exists because the three selections people actually make are exactly
 * those three, and clicking eleven chips to reach one of them is how the wrong language gets left
 * out of a release.
 */
@Composable
internal fun LocaleChoice(
    locales: List<String>,
    untranslated: Map<String, Int>,
    selected: Set<String>,
    enabled: Boolean,
    onChange: (Set<String>) -> Unit,
) {
    Column(Modifier.fillMaxWidth().gap(Space.SM)) {
        // `LFieldLabelStyle`, the label every other field on this page and on `/data` uses: it
        // carries the Micro face *with* its 0.08em tracking and does the shouting with
        // `text-transform`, so the catalogue holds a sentence-case source string rather than an
        // all-caps English one. Hand-rolled, this label rendered visibly untracked beside PROVIDER
        // a few pixels above it. Centred vertically, because top alignment floated it ~10px above
        // the baselines of the three buttons it shares the row with.
        Row(
            Modifier.fillMaxWidth().gap(Space.SM).flexWrap(FlexWrap.Wrap),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Span(LFieldLabelStyle.toModifier().toAttrs()) {
                Text(t("app.projects.slug.pretranslate_run.target_languages", "Target languages"))
            }
            LButton(
                t("app.projects.slug.pretranslate_run.all", "All"),
                { onChange(locales.toSet()) },
                variant = LButtonVariant.GHOST,
                size = LButtonSize.SM,
                enabled = enabled,
            )
            LButton(
                t("app.projects.slug.pretranslate_run.none", "None"),
                { onChange(emptySet()) },
                variant = LButtonVariant.GHOST,
                size = LButtonSize.SM,
                enabled = enabled,
            )
            LButton(
                t("app.projects.slug.pretranslate_run.behind_only", "Behind only"),
                { onChange(locales.filter { (untranslated[it] ?: 0) > 0 }.toSet()) },
                variant = LButtonVariant.GHOST,
                size = LButtonSize.SM,
                enabled = enabled,
            )
        }
        Row(Modifier.fillMaxWidth().gap(Space.SM).flexWrap(FlexWrap.Wrap)) {
            locales.forEach { locale ->
                LToggleChip(
                    text = localeInfo(locale).englishName,
                    selected = locale in selected,
                    onSelectedChange = { on ->
                        onChange(if (on) selected + locale else selected - locale)
                    },
                    count = untranslated[locale],
                    enabled = enabled,
                )
            }
        }
    }
}

/**
 * What the server says this would cost, before anybody presses the button.
 *
 * Three facts and no arithmetic of our own: the strings the selection matched, the credits it would
 * reserve, and what the workspace has left afterwards. The memory-prefillable count rides on the
 * first tile because it is the reason the second is smaller than the first, and a reader who cannot
 * see that reads the difference as a mistake.
 */
@Composable
internal fun EstimateRow(
    estimate: MtEstimateDto?,
    estimating: Boolean,
    error: String?,
    memoryOnly: Boolean,
) {
    if (error != null) {
        LAlert(
            t("app.projects.slug.pretranslate_run.we_couldnt_price_this_run", "We couldn't price this run"),
            error,
            variant = LAlertVariant.WARNING,
            dense = true,
        )
        return
    }
    if (estimate == null) {
        LMuted(
            if (estimating) {
                t("app.projects.slug.pretranslate_run.working_out_what_this_would_cost", "Working out what this would cost…")
            } else {
                t("app.projects.slug.pretranslate_run.pick_at_least_one_language_to", "Pick at least one language to price this run.")
            },
        )
        return
    }
    val strings = estimate.perLocale.sumOf { it.strings }
    val prefillable = estimate.perLocale.sumOf { it.memoryPrefillable }
    // **Three tiles on one line, because they are one sentence.** A non-compact `LStatTile` pins
    // `width: 100%`, so inside this wrapping row each tile claimed its own line: 430px of height
    // for three short numbers, read as three unrelated cards, with the primary action pushed that
    // far down. A flex basis wins over `width` on the main axis, so this is a call-site fix and
    // changes no other tile in the product.
    val tile = Modifier.styleModifier {
        property("flex", "1 1 14rem")
        property("min-width", "0")
    }
    Row(Modifier.fillMaxWidth().gap(Space.MD).flexWrap(FlexWrap.Wrap)) {
        LStatTile(
            modifier = tile,
            label = t("app.projects.slug.pretranslate_run.strings", "Strings"),
            value = formatCount(strings),
            delta = prefillable.takeIf { it > 0 }?.let {
                LStatDelta(
                    tPlural(
                        "app.projects.slug.pretranslate_run.count.already_in_memory",
                        it,
                        "{count} already in memory",
                        "{count} already in memory",
                    ),
                )
            },
        )
        // A run that costs nothing states so quietly. `readsAsZero` cannot see it — "None" has no
        // digits — so the accent was landing on the one figure in the estimate that should be the
        // quietest, making it louder than the two real numbers and than the Start button.
        val free = estimate.byok || memoryOnly
        LStatTile(
            modifier = tile,
            label = t("app.projects.slug.pretranslate_run.credits", "Credits"),
            value = if (free) {
                t("app.projects.slug.pretranslate_run.none_credits", "None")
            } else {
                formatCount(estimate.totalCredits)
            },
            emphasis = !free,
            tone = LBadgeVariant.ACCENT,
        )
        LStatTile(
            modifier = tile,
            label = t("app.projects.slug.pretranslate_run.left_after_this", "Left after this"),
            value = formatCount((estimate.creditsRemaining - estimate.totalCredits).coerceAtLeast(0)),
        )
    }
    estimate.blockedReason?.let {
        LAlert(
            t("app.projects.slug.pretranslate_run.this_run_cannot_start", "This run cannot start"),
            it,
            variant = LAlertVariant.WARNING,
        )
    }
}

/**
 * The run as it happens, and what it refused.
 *
 * **One progress readout, not one per language.** The job endpoint exposes a single aggregate
 * counter, so the old card drew the same bar under every locale heading with a paragraph explaining
 * that they were all the same number. [LTaskProgress] states it once — done, failed, and a detail
 * line naming the provider — and the languages are chips beside it, which is what they are.
 *
 * Rejections are rows on `MtJobDto.rejections` now. They used to be recovered by loading every key
 * name in the project and testing each line of the free-text error against all of them by prefix,
 * which cannot separate `save` from `save.confirm` at all.
 */
@Composable
internal fun PretranslateRunCard(
    job: MtJobDto,
    onOpenReceipt: () -> Unit,
    onCancel: (() -> Unit)?,
    cancelling: Boolean,
) {
    LCard(Modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth().gap(Space.XL)) {
            Row(
                Modifier.fillMaxWidth().gap(Space.SM).flexWrap(FlexWrap.Wrap),
            ) {
                LSectionHeading(
                    t("app.projects.slug.pretranslate_run.live_run_progress", "Live run progress"),
                    trf(
                        "app.projects.slug.pretranslate_run.provider_and_processed",
                        "{provider} · {done} of {total} processed",
                        "provider" to job.provider.displayName,
                        "done" to formatCount(job.done.coerceAtMost(job.total)),
                        "total" to formatCount(job.total),
                    ),
                    icon = { LZapIcon() },
                    actions = {
                        onCancel?.let {
                            LButton(
                                t("app.projects.slug.pretranslate_run.stop_this_run", "Stop this run"),
                                it,
                                variant = LButtonVariant.SECONDARY,
                                size = LButtonSize.SM,
                                enabled = !cancelling,
                                loading = cancelling,
                                leadingIcon = { LPauseIcon() },
                            )
                        }
                        LButton(
                            t("app.projects.slug.pretranslate_run.open_the_run_receipt", "Open the run receipt"),
                            onOpenReceipt,
                            variant = LButtonVariant.GHOST,
                            size = LButtonSize.SM,
                            leadingIcon = { LReceiptIcon() },
                        )
                    },
                )
            }
            Row(Modifier.fillMaxWidth().gap(Space.SM).flexWrap(FlexWrap.Wrap)) {
                LBadge(job.stateWords(), variant = job.stateVariant(), dot = true)
                job.locales.forEach { LChip(localeInfo(it).englishName) }
            }
            LTaskProgress(
                done = job.done.coerceIn(0, job.total.coerceAtLeast(0)),
                total = job.total.coerceAtLeast(0),
                failed = job.failed.coerceAtLeast(0),
                label = t("app.projects.slug.pretranslate_run.pre_translation_progress", "Pre-translation progress"),
                indeterminate = job.total <= 0 && job.state == MtJobState.RUNNING,
                detail = job.provider.displayName,
                // The badge one row up already says "Stopped", "Interrupted" or "Failed"; without
                // this the bar underneath it went on announcing `aria-busy="true"`, because the
                // counts of a run stopped at 100 of 210 stay short forever and the counts were all
                // the component had to go on. A listener was told work was still in progress on a
                // run that had terminated, and waited for an update nothing would ever send.
                complete = job.state != MtJobState.RUNNING,
            )
            if (job.rejected > 0 || job.rejections.isNotEmpty()) {
                RejectionTable(job)
            }
        }
    }
}

@Composable
private fun RejectionTable(job: MtJobDto) {
    LSectionHeading(
        t("app.projects.slug.pretranslate_run.rejected_keys", "Rejected keys"),
        t("app.projects.slug.pretranslate_run.review_each_reason_before_translating_these", "Review each reason before translating these keys manually."),
    )
    if (job.rejections.isEmpty()) {
        LMuted(
            tPlural(
                "app.projects.slug.pretranslate_run.rejected_without_detail",
                job.rejected,
                "The run reported {count} rejected result and recorded no detail for it.",
                "The run reported {count} rejected results and recorded no detail for them.",
            ),
        )
        return
    }
    LTable(caption = t("app.projects.slug.pretranslate_run.machine_translation_rejections", "Machine translation rejections")) {
        LTableHead {
            LTableRow {
                LTableHeaderCell { Text(t("app.projects.slug.pretranslate_run.key", "Key")) }
                LTableHeaderCell { Text(t("app.projects.slug.pretranslate_run.reason", "Reason")) }
            }
        }
        LTableBody {
            job.rejections.forEach { rejection ->
                LTableRow {
                    LTableCell { Span(Modifier.monoFont().toAttrs()) { Text(rejection.keyName) } }
                    LTableCell { Text(rejection.reason) }
                }
            }
        }
    }
    // The cap, not a parsing shortfall: the server keeps the first fifty and counts the rest.
    val missingDetails = (job.rejected - job.rejections.size).coerceAtLeast(0)
    if (missingDetails > 0) {
        LAlert(
            t("app.projects.slug.pretranslate_run.some_rejection_details_are_unavailable", "Some rejection details are unavailable"),
            tf(
                "app.projects.slug.pretranslate_run.this_run_records_the_first_shown",
                "This run records the first {shown} rejections in full. {rest} more were counted " +
                    "and not written down — they are almost always the same problem.",
                "shown" to job.rejections.size,
                "rest" to missingDetails,
            ),
            variant = LAlertVariant.WARNING,
        )
    }
}

/**
 * Written words, never `name.lowercase()`.
 *
 * `abandoned` and `cancelled` are read before the state, because both are FAILED on the wire and
 * "the server restarted" and "you stopped it" are not the same news as "the provider broke".
 */
private fun MtJobDto.stateWords(): String = when {
    cancelled -> tr("app.projects.slug.pretranslate_run.stopped", "Stopped")
    abandoned -> tr("app.projects.slug.pretranslate_run.interrupted", "Interrupted")
    state == MtJobState.RUNNING -> tr("app.projects.slug.pretranslate_run.running", "Running")
    state == MtJobState.DONE -> tr("app.projects.slug.pretranslate_run.done", "Done")
    else -> tr("app.projects.slug.pretranslate_run.failed", "Failed")
}

private fun MtJobDto.stateVariant(): LBadgeVariant = when {
    cancelled || abandoned -> LBadgeVariant.NEUTRAL
    state == MtJobState.RUNNING -> LBadgeVariant.INFO
    state == MtJobState.DONE -> LBadgeVariant.SUCCESS
    else -> LBadgeVariant.DANGER
}
